import clsx from 'clsx';

import { formatMoscowRelativeDateTime } from '@/shared/lib/format';
import { RADIUS, Squircle, StaticSquircle } from '@/shared/ui';

import { getMatchResult } from '../lib/match';

import type { Match } from '../model/types';

import { TeamCrest } from './TeamCrest';
import styles from './MatchCard.module.scss';

interface MatchCardProps {
  match: Match;
  /**
   * Что открывает тап, решает витрина: «Текущие» — шторку Прогноза,
   * «Завершённые» — статистику. Карточка не знает, в какой она ленте,
   * и не выбирает за неё: нет `onClick` — не кликается. Витрина ставит
   * его по `canOpenPredictionForm` / `canOpenStats` из `lib/match`.
   */
  onClick?: (() => void) | undefined;
}

/**
 * Карточка матча в ленте Прогнозов (узел 161:2959, GameCard).
 *
 * Синий фон + жёлтый чип «В процессе» — не статус матча (LIVE), а то, что
 * игрок уже поставил хотя бы один Прогноз и ждёт результата: узел 438:51166
 * показывает этот чип на матче с временем «Завтра», когда матч заведомо
 * ещё не идёт. Итог Прогноза (зелёный «Успех» / красный «Провал», те же
 * 438:51166 / 438:51167) берётся из статуса Прогноза (WON/VOID_WON/LOST),
 * который считает сервер — см. `getMatchResult`.
 */
export function MatchCard({ match, onClick }: MatchCardProps) {
  const result = getMatchResult(match);
  // Не через `availableTypes`: в витрине сыгранных пула дня нет и список
  // пуст, а чип «В процессе» нужен и там — Прогноз может остаться
  // нерассчитанным (например «Первый гол» без забившей команды)
  const hasProgress = result === null && match.myPredictions.length > 0;
  const isClickable = onClick !== undefined;

  const content = (
    <>
      <header className={styles.header}>
        <time className={styles.time} dateTime={match.startsAt}>
          {formatMoscowRelativeDateTime(match.startsAt)}
        </time>
        {result === 'success' ? (
          <span className={styles.chipSuccess}>
            Успех
            <img className={styles.chipIcon} src="/images/header/fire.svg" alt="" aria-hidden />
            {/* Сумма по всем типам, а не по выигравшим: у проигранного
                `pointsAwarded` равен нулю, и на смешанном матче в чипе
                оказывается ровно то, что игроку начислено */}
            <span className={styles.chipPoints}>
              {match.myPredictions.reduce((sum, p) => sum + p.pointsAwarded, 0)}
            </span>
          </span>
        ) : result === 'failure' ? (
          <span className={styles.chipFailure}>
            Провал
            <img
              className={styles.chipFailureIcon}
              src="/images/matches/oops-minus.svg"
              alt=""
              aria-hidden
            />
          </span>
        ) : hasProgress ? (
          <span className={styles.chip}>В процессе</span>
        ) : !match.isOpenForPredictions ? (
          <span className={styles.closed}>Приём закрыт</span>
        ) : null}
      </header>

      <div className={styles.teams}>
        <TeamColumn team={match.homeTeam} />
        <span className={styles.versus}>X</span>
        <TeamColumn team={match.awayTeam} />
      </div>
    </>
  );

  return (
    <Squircle
      asChild={isClickable}
      cornerRadius={RADIUS.xl}
      className={clsx(
        styles.card,
        (hasProgress || result !== null) && styles.cardProgress,
      )}
      data-testid="match-card"
    >
      {isClickable ? (
        <button type="button" onClick={onClick}>
          {content}
        </button>
      ) : (
        content
      )}
    </Squircle>
  );
}

function TeamColumn({ team }: { team: Match['homeTeam'] }) {
  return (
    <div className={styles.team}>
      <StaticSquircle
        width={100}
        height={100}
        cornerRadius={RADIUS.lg}
        className={styles.crest}
      >
        <TeamCrest
          name={team.name}
          logoUrl={team.logoUrl}
          imageClassName={styles.crestImage}
          fallbackClassName={styles.crestFallback}
        />
      </StaticSquircle>
      <p className={styles.teamName}>{team.name}</p>
    </div>
  );
}
